import { IconButton } from 'design-system';
import { DownloadIcon } from 'lucide-react';

import type { ChartType } from '@/features/charts/types/highcharts';
import { canExportWidget } from '@/features/dashboard';
import type { FormAppWidget } from '@/features/dashboard';
import { useCsvWidgetExportAction, useExportChartAction } from '@/features/data-hub';
import { useOptionalMarketWidget } from '@/features/markets/components/contexts/MarketWidgetContext';
import ExportWidgetButton from '@/features/markets/components/ExportWidgetButton';
import type { BaseTabType } from '@/features/markets/types';
import CustomDownloadChartButton from '../CustomDownloadChartButton';

export const isTabExportSupported = (tab: BaseTabType): boolean => {
  if (tab.type === 'graph' && tab.metadata?.graphType === 'static') {
    return false;
  }

  if (tab.type === 'graph' || tab.type === 'csvtable' || tab.type === 'minitable') {
    return true;
  }

  if (tab.type === 'spot_history_table') {
    return Boolean(tab.columns.length) && canExportWidget(tab as FormAppWidget);
  }

  return canExportWidget(tab as FormAppWidget);
};

const GraphExportButton = ({
  graphType,
  isFullScreen,
  metadata,
  tabName
}: {
  graphType: ChartType;
  isFullScreen: boolean;
  metadata?: Record<string, string>;
  tabName: string;
}) => {
  const { disabled, exportChart } = useExportChartAction({
    graphType,
    isFullScreen,
    metadata,
    tabName
  });

  return (
    <IconButton
      disabled={disabled}
      label='Export chart'
      variant='ghost'
      onClick={exportChart}
    >
      <DownloadIcon />
    </IconButton>
  );
};

const CsvExportButton = ({ tabName }: { tabName: string }) => {
  const { disabled, exportCsv } = useCsvWidgetExportAction({ tabName });

  return (
    <IconButton
      disabled={disabled}
      label='Download as CSV'
      variant='ghost'
      onClick={exportCsv}
    >
      <DownloadIcon />
    </IconButton>
  );
};

const TabExportButton = ({ tab }: { tab: BaseTabType }) => {
  const { isFullScreen } = useOptionalMarketWidget();

  if (!isTabExportSupported(tab)) {
    return null;
  }

  if (tab.type === 'graph') {
    return tab.metadata?.graphType === 'custom' ? (
      <CustomDownloadChartButton
        metadata={tab.metadata}
        tabName={tab.name}
      />
    ) : (
      <GraphExportButton
        graphType={tab.metadata?.graphType as ChartType}
        isFullScreen={isFullScreen}
        metadata={tab.metadata}
        tabName={tab.name}
      />
    );
  }
  if (tab.type === 'csvtable') {
    return <CsvExportButton tabName={tab.name} />;
  }
  return <ExportWidgetButton tab={tab} />;
};

export default TabExportButton;
